import React from 'react'

import type { MascotEarStyle } from '../mascot-types'
import {
    coatFill,
    FLAT_COAT_STROKE_WIDTH,
    flatCoatFill,
} from './mascot-svg-shared'

/**
 * Ear artwork lives in one canonical space so a shape is drawn once and
 * reused by every species.
 *
 * Local space is a LEFT ear whose **base is centred on the origin** and a
 * little below it (+y), so the anchor can be placed *inside* the skull and
 * the base disappears under the head instead of floating beside it. Erect
 * shapes grow toward -y; hanging shapes fall toward +y and lean outward
 * (-x). The right ear is the same path mirrored across the head's vertical
 * axis, so nothing needs a second drawing.
 */
type EarArtCommon = {
    /**
     * Shapes that fall below the attachment point instead of rising above it.
     * Splay has to be applied in the opposite direction for these — the same
     * rotation that tips an erect ear outward would swing a hanging one
     * inward, tucking it behind the head.
     */
    hangs?: boolean
    /**
     * Drawn after the skull. A downward fold sitting on the crown would
     * vanish behind the head if it painted with the rest of the ears.
     */
    front?: boolean
    /**
     * Multiplier on the species splay. Defaults to -1 (outward) for erect
     * ears and {@link HANGING_SPLAY_FACTOR} for hanging ones. 0 keeps the
     * author's own tilt.
     */
    splayFactor?: number
}

/** Path-drawn ear in the shared local space. */
type PathEarArt = EarArtCommon & {
    outline: string
    /** Inner ear, filled with the secondary coat. */
    inner?: string
    /**
     * Tip lying forward over the ear, drawn on top of the outline in the coat
     * colour with a shade over it. This is the back of the ear, so it's coat
     * coloured rather than inner-ear coloured.
     */
    fold?: string
}

/**
 * Designer polygons in their original SVG space. We translate/scale the
 * group to sit on the skull — we do not redraw the shapes.
 */
type AuthorEarArt = EarArtCommon & {
    author: {
        originX: number
        originY: number
        scale: number
        strokeWidth: number
        outline: string
        inner?: string
        fold?: string
    }
}

type EarArt = PathEarArt | AuthorEarArt

function isAuthorEar(art: EarArt): art is AuthorEarArt {
    return 'author' in art
}

const EAR_ART: Record<MascotEarStyle, EarArt> = {
    /** Prick ear — pomeranian / shepherd / cat: wide base, softly curved sides. */
    pointed: {
        outline:
            'M-15 10 C-13 -6 -8 -22 -1 -33 C0 -34.5 1 -34.5 2 -33 C9 -22 13 -6 15 10 Z',
        inner: 'M-9 8 C-8 -3 -5 -15 0 -24 C5 -15 8 -3 9 8 Z',
    },
    /** Bat ear — frenchie: wide base, tall rounded top. */
    upright: {
        outline: 'M-15 10 C-18 -8 -13 -32 0 -32 C13 -32 18 -8 15 10 Z',
        inner: 'M-9 8 C-11 -6 -8 -23 0 -23 C8 -23 11 -6 9 8 Z',
    },
    /**
     * Button ear — pug / jack russell: a short flap folded forward over the
     * canal. A button ear is a small fold, so drawing it as a plain blob
     * reads as an oval stuck to the head rather than an ear.
     */
    button: {
        outline:
            'M-8 -13 C-14.5 -10.5 -17.5 -4 -16.5 3 C-15.5 9.5 -10.5 12.5 -6 10 C-2.5 8 -1.8 3.5 -3.5 -0.8 C-5 -4.5 -5 -8.5 -1.2 -11 Z',
        fold: 'M-8 -13 C-14.5 -10.5 -17.5 -4 -16.5 3 C-12.5 2 -9.5 -0.8 -8.6 -4 C-7.9 -6.8 -7.6 -9 -1.2 -11 Z',
        hangs: true,
    },
    /**
     * Folded ear — Scottish Fold / rose. Polygons are the designer's left
     * ear, verbatim; the right ear is the shared mirror. Drawn in front of
     * the skull so the downward fold sits on the crown instead of vanishing
     * behind it.
     */
    folded: {
        front: false,
        splayFactor: 0,
        author: {
            // Inner-top of the designer ear, shifted so the flap sits on the
            // crown and the tip hangs onto the forehead — same colour as the
            // skull, so if the whole shape lands on the face it vanishes.
            originX: 103,
            originY: 130,
            scale: 0.32,
            strokeWidth: 6,
            outline: '40,100 100,90 125,110 75,170',
            inner: '50,100 90,100 117,120 85,160',
            fold: '20,100 100,90 50,150',
        },
    },
    /** Drop ear — golden retriever: hangs close to the head, rounded tip. */
    floppy: {
        outline:
            'M-9 -10 C-20 -7 -25 6 -24 21 C-23 36 -16 45 -7 43 C-2 41 0 34 -2 26 C-5 14 -6 2 -1 -7 Z',
        hangs: true,
    },
    /** Pendant ear — hound: longer and narrower than a drop. */
    droopy: {
        outline:
            'M-9 -10 C-19 -7 -23 8 -22 28 C-21 50 -14 61 -6 58 C-1 56 1 46 -1 35 C-4 19 -5 3 -1 -7 Z',
        hangs: true,
    },
    /** Composite — see `oneUpPair`; never drawn directly. */
    one_up: {
        outline:
            'M-15 10 C-13 -6 -8 -22 -1 -33 C0 -34.5 1 -34.5 2 -33 C9 -22 13 -6 15 10 Z',
        inner: 'M-9 8 C-8 -3 -5 -15 0 -24 C5 -15 8 -3 9 8 Z',
    },
}

/** Where a species hangs its ears, and how big they are there. */
export type MascotEarAnchor = {
    /**
     * Ear root for the LEFT ear, in the species' portrait space. Put this
     * INSIDE the head outline — the ear base is drawn around it and needs to
     * be covered by the skull, which paints afterwards.
     */
    x: number
    y: number
    /** Head centre x — the axis the right ear mirrors across. */
    headCx: number
    /** Horizontal scale. */
    scale: number
    /**
     * Vertical scale, defaulting to `scale`. Split from the horizontal one
     * because a rabbit's ear is narrow AND long — scaling the shared shape
     * uniformly to get the length also widens the base until it no longer
     * fits inside the skull.
     */
    scaleY?: number
    /**
     * Outward splay in degrees. Ears growing straight up out of a round head
     * look stuck on; a bit of rotation makes them sit on the skull.
     */
    rotate?: number
    /**
     * Per-style multiplier on both axes (1 = unchanged). A rabbit's upright
     * ears want to be huge, but the same size on a lop would hang it off the
     * bottom of the frame.
     */
    scaleByStyle?: Partial<Record<MascotEarStyle, number>>
    /**
     * Per-style nudge in portrait space, applied before mirroring so a
     * positive x moves both ears outward.
     */
    offsetByStyle?: Partial<Record<MascotEarStyle, { x?: number; y?: number }>>
    /**
     * Which two shapes `one_up` pairs. Defaults to pointed + floppy, but
     * dogs pair pointed with rose and a rabbit's half-lop is upright + lop.
     */
    oneUpPair?: [MascotEarStyle, MascotEarStyle]
    /** Rabbits read better with pink inners than coat-secondary ones. */
    innerFill?: string
}

const HANGING_SPLAY_FACTOR = 0.45

type EarScale = { x: number; y: number }

function earScale(anchor: MascotEarAnchor, style: MascotEarStyle): EarScale {
    const multiplier = anchor.scaleByStyle?.[style] ?? 1
    return {
        x: anchor.scale * multiplier,
        y: (anchor.scaleY ?? anchor.scale) * multiplier,
    }
}

function earNudge(
    anchor: MascotEarAnchor,
    style: MascotEarStyle,
): { x: number; y: number } {
    const n = anchor.offsetByStyle?.[style]
    // Applied to the left-ear position; the right ear mirrors, so a positive
    // x here would move both inward. Negate so the public offset is outward.
    return { x: -(n?.x ?? 0), y: n?.y ?? 0 }
}

function Ear({
    art,
    anchor,
    scale,
    side,
    flat,
    nudge,
}: {
    art: EarArt
    anchor: MascotEarAnchor
    scale: EarScale
    side: 'left' | 'right'
    flat: boolean
    nudge: { x: number; y: number }
}) {
    // Mirroring the right ear also flips the rotation, so both splay outward
    // from the same `rotate` value. Hanging ears get a fraction of it: gravity
    // pulls them down rather than out, and a full splay throws the silhouette
    // so wide that every other style has to render smaller to share a frame.
    const splayFactor =
        art.splayFactor ?? (art.hangs ? HANGING_SPLAY_FACTOR : -1)
    const splay = (anchor.rotate ?? 0) * splayFactor
    const spin = `rotate(${splay})`
    const x = anchor.x + nudge.x
    const y = anchor.y + nudge.y
    const transform =
        side === 'left'
            ? `translate(${x} ${y}) scale(${scale.x} ${scale.y}) ${spin}`
            : `translate(${2 * anchor.headCx - x} ${y}) scale(${-scale.x} ${scale.y}) ${spin}`

    return (
        <g transform={transform}>
            {isAuthorEar(art) ? (
                <AuthorEar
                    art={art.author}
                    flat={flat}
                    speciesScale={scale}
                    innerFill={anchor.innerFill}
                />
            ) : (
                <PathEar art={art} flat={flat} innerFill={anchor.innerFill} />
            )}
        </g>
    )
}

function PathEar({
    art,
    flat,
    innerFill,
}: {
    art: PathEarArt
    flat: boolean
    innerFill?: string
}) {
    return (
        <>
            <path d={art.outline} {...(flat ? flatCoatFill : coatFill)} />
            {art.inner ? (
                <path
                    d={art.inner}
                    fill={innerFill ?? 'var(--coat-secondary)'}
                    opacity={flat ? 0.7 : 0.85}
                />
            ) : null}
            {art.fold ? (
                <>
                    <path d={art.fold} {...(flat ? flatCoatFill : coatFill)} />
                    {/* Shade rather than a second colour, so the fold reads on
                        any coat without needing per-coat maths. Weighted
                        enough to read as a separate plane — too faint and the
                        ear just looks like it has a smudge near the tip. */}
                    <path d={art.fold} fill="#000" opacity={0.2} />
                </>
            ) : null}
        </>
    )
}

function RoundedPolygon({
    points,
    fill,
    stroke,
    strokeWidth,
    opacity,
}: {
    points: string
    fill: string
    stroke: string
    strokeWidth: number
    opacity?: number
}) {
    return (
        <polygon
            points={points}
            fill={fill}
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeLinejoin="round"
            opacity={opacity}
        />
    )
}

/**
 * Designer polygons, placed so `origin` is the shared ear anchor. The fat
 * same-colour stroke is how the SVG gets its rounded corners — keep it.
 */
function AuthorEar({
    art,
    flat,
    speciesScale,
    innerFill,
}: {
    art: AuthorEarArt['author']
    flat: boolean
    speciesScale: EarScale
    innerFill?: string
}) {
    const coat = 'var(--coat-primary)'
    const inner = innerFill ?? 'var(--coat-secondary)'
    const round = art.strokeWidth
    const rim =
        round +
        (flat ? FLAT_COAT_STROKE_WIDTH : coatFill.strokeWidth) /
            (art.scale * Math.abs(speciesScale.x))

    return (
        <g
            transform={`scale(${art.scale}) translate(${-art.originX} ${-art.originY})`}
        >
            <RoundedPolygon
                points={art.outline}
                fill="none"
                stroke="var(--coat-line)"
                strokeWidth={rim}
            />
            <RoundedPolygon
                points={art.outline}
                fill={coat}
                stroke={coat}
                strokeWidth={round}
            />
            {/* Underside of the standing ear — darker than the folded flap,
                matching the designer's dark base vs bright fold. */}
            <RoundedPolygon
                points={art.outline}
                fill="#000"
                stroke="#000"
                strokeWidth={round}
                opacity={0.22}
            />
            {art.inner ? (
                <>
                    <RoundedPolygon
                        points={art.inner}
                        fill={inner}
                        stroke={inner}
                        strokeWidth={round}
                    />
                    <RoundedPolygon
                        points={art.inner}
                        fill="#000"
                        stroke="#000"
                        strokeWidth={round}
                        opacity={0.12}
                    />
                </>
            ) : null}
            {art.fold ? (
                <RoundedPolygon
                    points={art.fold}
                    fill={coat}
                    stroke={coat}
                    strokeWidth={round}
                />
            ) : null}
        </g>
    )
}

/**
 * Both ears for a species. Draw `layer="back"` BEFORE the head so hanging
 * shapes tuck behind the skull, and `layer="front"` AFTER so a downward fold
 * can sit on the crown.
 */
export default function MascotEars({
    style,
    anchor,
    flat = false,
    layer = 'back',
}: {
    style: MascotEarStyle
    anchor: MascotEarAnchor
    /**
     * Draw without the coat outline, for species whose artwork is flat
     * (no strokes). Same shapes either way — only the rendering differs, so
     * ear styles stay swappable across both art styles.
     */
    flat?: boolean
    /**
     * `back` paints with the fur, before the skull. `front` paints after
     * the skull — only styles marked `front` (the downward fold) belong
     * there. Species must call both, sandwiching the head.
     */
    layer?: 'back' | 'front'
}) {
    // The mismatched look is a pairing, not its own artwork. Each side is
    // scaled by its own shape so a half-lop keeps the tall ear tall.
    const [leftStyle, rightStyle]: [MascotEarStyle, MascotEarStyle] =
        style === 'one_up'
            ? (anchor.oneUpPair ?? ['pointed', 'floppy'])
            : [style, style]

    const show = (art: EarArt): boolean =>
        layer === 'front' ? !!art.front : !art.front

    const leftArt = EAR_ART[leftStyle]
    const rightArt = EAR_ART[rightStyle]

    return (
        <>
            {show(leftArt) ? (
                <Ear
                    art={leftArt}
                    anchor={anchor}
                    scale={earScale(anchor, leftStyle)}
                    side="left"
                    flat={flat}
                    nudge={earNudge(anchor, leftStyle)}
                />
            ) : null}
            {show(rightArt) ? (
                <Ear
                    art={rightArt}
                    anchor={anchor}
                    scale={earScale(anchor, rightStyle)}
                    side="right"
                    flat={flat}
                    nudge={earNudge(anchor, rightStyle)}
                />
            ) : null}
        </>
    )
}
